// client/src/components/TrustBanner.jsx
// Animated banner showing trust signals and contact info

import React, { useState, useEffect } from 'react';
import { FaStar, FaPhone, FaClock, FaTruck, FaHeart, FaShieldAlt, FaCheckCircle, FaInfoCircle } from 'react-icons/fa';
import { API_URL } from '../config';
import './TrustBanner.css';

// Function to get icon component based on icon type
const getIconComponent = (iconType) => {
  switch (iconType) {
    case 'star':
      return <FaStar />;
    case 'phone':
      return <FaPhone />;
    case 'clock':
      return <FaClock />;
    case 'truck':
      return <FaTruck />;
    case 'heart':
      return <FaHeart />;
    case 'shield':
      return <FaShieldAlt />;
    case 'check':
      return <FaCheckCircle />;
    case 'info':
      return <FaInfoCircle />;
    default:
      return <FaStar />;
  }
};

const TrustBanner = () => {
  const [trustItems, setTrustItems] = useState([]);
  const [bannerEnabled, setBannerEnabled] = useState(true);
  const [bannerSettings, setBannerSettings] = useState({
    scrollSpeed: 40,
    backgroundColor: '#003366',
    textColor: '#ffffff',
    iconColor: '#ffcc00',
    height: 12
  });
  const [loading, setLoading] = useState(true);

  // Fetch trust banner items from site settings
  useEffect(() => {
    const fetchSettings = async () => {
      const defaultItems = [
        { icon: 'star', text: '4.8/5 TrustPilot Rating - Based on 1,200+ reviews', link: null },
        { icon: 'phone', text: 'Call us: 01482 123456 - Mon-Fri, 7:30am-4:30pm', link: 'tel:01482123456' },
        { icon: 'clock', text: 'Next Day Delivery Available on selected items', link: null },
        { icon: 'truck', text: 'Free Delivery on orders over £750', link: null },
        { icon: 'check', text: '30-Day Money Back Guarantee', link: null },
        { icon: 'heart', text: '24/7 Customer Support - We\'re here to help', link: null },
      ];

      try {
        const response = await fetch(`${API_URL}/site-settings`);
        if (response.ok) {
          const settings = await response.json();

          // Set banner enabled status
          if (settings.trustBannerEnabled !== undefined) {
            setBannerEnabled(settings.trustBannerEnabled);
          }

          // Set banner styling settings
          if (settings.trustBannerSettings) {
            setBannerSettings(settings.trustBannerSettings);
          }

          if (settings.trustBanner && Array.isArray(settings.trustBanner)) {
            // Filter enabled items
            const items = settings.trustBanner.filter(item => item.enabled);
            setTrustItems(items);
          } else {
            console.warn('⚠️ No trustBanner array found in settings, using defaults');
            setTrustItems(defaultItems);
          }
        } else {
          console.warn('⚠️ API response not ok:', response.status);
          setTrustItems(defaultItems);
        }
      } catch (error) {
        console.error('❌ Failed to fetch trust banner settings:', error);
        setTrustItems(defaultItems);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Don't render anything if banner is disabled
  if (!bannerEnabled) {
    return null;
  }

  // Create CSS variables for dynamic styling
  const bannerStyle = {
    '--trust-scroll-speed': `${bannerSettings.scrollSpeed}s`,
    '--trust-bg-color': bannerSettings.backgroundColor,
    '--trust-text-color': bannerSettings.textColor,
    '--trust-icon-color': bannerSettings.iconColor,
    '--trust-height': `${bannerSettings.height}px`,
  };

  const handleItemClick = (link) => {
    if (link) {
      if (link.startsWith('tel:') || link.startsWith('mailto:')) {
        window.location.href = link;
      } else if (link.startsWith('http://') || link.startsWith('https://')) {
        window.open(link, '_blank');
      } else {
        window.location.href = link;
      }
    }
  };

  return (
    <div className="trust-banner" style={bannerStyle}>
      <div className="trust-banner-content">
        {/* Triple items for seamless loop with no gaps */}
        {[...trustItems, ...trustItems, ...trustItems].map((item, index) => (
          <div
            key={index}
            className={`trust-item ${item.link ? 'clickable' : ''}`}
            onClick={() => handleItemClick(item.link)}
            role={item.link ? 'button' : 'presentation'}
            tabIndex={item.link ? 0 : -1}
            onKeyPress={(e) => item.link && (e.key === 'Enter' || e.key === ' ') && handleItemClick(item.link)}
          >
            <span className="trust-icon">{getIconComponent(item.icon)}</span>
            <span className="trust-text">{item.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TrustBanner;
